/**
 * Rich text editor for KB articles.
 * Uses TipTap with StarterKit, Link, Image.
 */

import React from 'react';
import styled from '@emotion/styled';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import {
  BoldOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Button, Space, Input, Modal, message } from 'antd';
import { isValidImageUrl, isValidLinkUrl } from '../../utils/urlValidation';

const EditorContentWrapper = styled.div`
  .ProseMirror:focus {
    outline: none;
  }
`;

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [linkModalVisible, setLinkModalVisible] = React.useState(false);
  const [imageModalVisible, setImageModalVisible] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-blue-600 underline' },
        validate: (href) => /^(https?:\/\/|mailto:|tel:)/.test(href),
      }),
      Image.configure({
        HTMLAttributes: { class: 'max-w-full h-auto' },
        allowBase64: true,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: {
      transformPastedHTML: (html) =>
        html
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
          .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
          .replace(/javascript:/gi, '')
          .replace(/vbscript:/gi, '')
          .replace(/data:(?:text\/html|application\/x?javascript)/gi, ''),
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file && file.size <= 5 * 1024 * 1024) {
              const reader = new window.FileReader();
              reader.onload = () => {
                const dataUrl = reader.result as string;
                if (dataUrl.startsWith('data:image/')) {
                  const schema = view.state.schema;
                  const node = schema.nodes.image?.create({ src: dataUrl });
                  if (node) {
                    view.dispatch(view.state.tr.replaceSelectionWith(node));
                  }
                }
              };
              reader.readAsDataURL(file);
            }
            return true;
          }
        }
        return false;
      },
    },
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  const addLink = (): void => {
    if (!linkUrl?.trim()) return;
    if (!isValidLinkUrl(linkUrl)) {
      message.warning('Invalid URL. Use https, http, mailto, or tel.');
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    setLinkUrl('');
    setLinkModalVisible(false);
  };

  const addImage = (): void => {
    if (!imageUrl?.trim()) return;
    if (!isValidImageUrl(imageUrl)) {
      message.warning('Invalid URL. Use https or Firebase Storage image URL.');
      return;
    }
    editor?.chain().focus().setImage({ src: imageUrl }).run();
    setImageUrl('');
    setImageModalVisible(false);
  };

  const setLink = (): void => {
    const prev = editor?.getAttributes('link').href;
    setLinkUrl(prev || '');
    setLinkModalVisible(true);
  };

  if (!editor) return null;

  return (
    <div>
      <div
        style={{
          border: '1px solid #d9d9d9',
          borderBottom: 'none',
          padding: '8px',
          backgroundColor: '#fafafa',
        }}
      >
        <Space wrap>
          <Button
            type={editor.isActive('bold') ? 'primary' : 'default'}
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={disabled}
          />
          <Button
            type={editor.isActive('italic') ? 'primary' : 'default'}
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={disabled}
          />
          <Button
            type={editor.isActive('bulletList') ? 'primary' : 'default'}
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            disabled={disabled}
          />
          <Button
            type={editor.isActive('orderedList') ? 'primary' : 'default'}
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={disabled}
          />
          <Button
            type={editor.isActive('link') ? 'primary' : 'default'}
            icon={<LinkOutlined />}
            onClick={setLink}
            disabled={disabled}
          />
          <Button
            icon={<PictureOutlined />}
            onClick={() => setImageModalVisible(true)}
            disabled={disabled}
          />
        </Space>
      </div>
      <EditorContentWrapper
        style={{
          border: '1px solid #d9d9d9',
          minHeight: '200px',
          padding: '12px',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
        }}
      >
        <EditorContent editor={editor} />
      </EditorContentWrapper>
      <Modal
        title="Add link"
        open={linkModalVisible}
        onOk={addLink}
        onCancel={() => setLinkModalVisible(false)}
        okText="Add"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter URL"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={addLink}
        />
      </Modal>
      <Modal
        title="Add image"
        open={imageModalVisible}
        onOk={addImage}
        onCancel={() => setImageModalVisible(false)}
        okText="Add"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          onPressEnter={addImage}
        />
      </Modal>
    </div>
  );
};
